import React, { useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { X, Send } from "lucide-react";
import api, { formatApiErrorDetail } from "../../lib/api";

export function audienceLabel(a) {
  return { all: "All users", musicians: "Musicians", bookers: "Bookers" }[a] || a;
}

function Shell({ children, onClose, testid, wide }) {
  return createPortal(
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={onClose}>
      <div className={`bg-white rounded-2xl w-full ${wide ? "max-w-2xl" : "max-w-md"} max-h-[90vh] overflow-y-auto p-5 space-y-4`}
        onClick={(e) => e.stopPropagation()} data-testid={testid}>
        {children}
      </div>
    </div>,
    document.body,
  );
}

export function AnnouncementPreview({ data, onClose }) {
  const [view, setView] = useState("app");
  return (
    <Shell onClose={onClose} testid="announcement-preview-modal" wide>
      <div className="flex items-center justify-between">
        <div className="flex gap-1.5">
          {[["app", "In-app"], ["email", "Email"]].map(([v, l]) => (
            <button key={v} onClick={() => setView(v)} data-testid={`preview-tab-${v}`}
              className={`px-3 py-1.5 rounded-full text-sm font-medium ${view === v ? "bg-sage-500 text-white" : "bg-ivory-100 text-ink-700"}`}>{l}</button>
          ))}
        </div>
        <button onClick={onClose} className="text-ink-500" data-testid="announcement-preview-close"><X size={18} /></button>
      </div>
      <p className="text-xs text-ink-500">Shown with your own name and invite link.</p>
      {view === "app" ? (
        <div className="rounded-2xl border border-ivory-200 p-4 space-y-3" data-testid="preview-inapp">
          <div className="font-heading text-lg font-semibold">{data.title}</div>
          <div className="text-sm text-ink-700 whitespace-pre-line">{data.body_before ?? data.body}</div>
          {data.include_invite && (
            <div className="text-sm rounded-xl bg-ivory-100 p-2.5 break-all">
              <div className="label-overline mb-1">Your personal invite link</div>
              {data.invite_link}
            </div>
          )}
          {data.body_after && <div className="text-sm text-ink-700 whitespace-pre-line">{data.body_after}</div>}
        </div>
      ) : (
        <iframe title="Email preview" srcDoc={data.email_html} className="w-full h-[480px] rounded-xl border border-ivory-200" data-testid="preview-email-frame" />
      )}
    </Shell>
  );
}

export function SendConfirm({ id, title, audience, audienceKey, onClose, onSent }) {
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);

  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/announcements/${id}/send`, { confirm_recipients: audience.total });
      toast.success(`Sending to ${data.recipients} people${data.email_dry_run ? " (email dry run)" : ""}`);
      onSent();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      setBusy(false);
    }
  };

  return (
    <Shell onClose={onClose} testid="announcement-send-confirm">
      <div className="font-heading text-lg font-semibold">Send "{title}"?</div>
      <ul className="text-sm text-ink-700 space-y-1">
        <li><strong>{audience.total}</strong> people in total</li>
        <li>{audience.in_app} in-app notifications</li>
        <li>{audience.email} emails{audience.email_dry_run ? " (dry run — preview environment)" : ""}</li>
      </ul>
      <p className="text-sm text-terracotta-600">This can't be undone or edited after sending.</p>
      <label className="block">
        <span className="text-sm text-ink-600">Type <strong>SEND</strong> to confirm</span>
        <input className="input-field mt-1" value={typed} onChange={(e) => setTyped(e.target.value)} data-testid="announcement-confirm-input" />
      </label>
      <div className="flex justify-end gap-2">
        <button className="btn-secondary" onClick={onClose} data-testid="announcement-confirm-cancel">Cancel</button>
        <button className="btn-primary !bg-terracotta-500 hover:!bg-terracotta-600 disabled:opacity-50 disabled:cursor-not-allowed" disabled={typed !== "SEND" || busy} onClick={send} data-testid="announcement-confirm-send">
          <Send size={16} /> Send to {audience.total} ({audienceLabel(audienceKey)})
        </button>
      </div>
    </Shell>
  );
}
